import { Badge } from '@mantine/core';
import type { ReactNode } from 'react';

/**
 * The status vocabulary — every non-acquisition-kind state chip in the app,
 * one place. Deliberately separate from {@link KindBadge}: `KindBadge` types
 * an *acquisition* (`buy` / `sale` / `trade_in` / ...); `StatusBadge` types a
 * *state* (a show is live, a record is a draft, a queued write is still
 * syncing). The two overlap in one place on purpose — `sale`/`buy`/`trade`
 * read identically to their `KindBadge` cousins where a screen needs the
 * word without the full acquisition-kind semantics (e.g. a quick-action
 * label) — but they are two components, not one, per the ratified vocabulary
 * mapping (`docs/redesign-decision-brief.md` §2.2: "Adjacent, not identical
 * ... Two primitives, not one; name them so.").
 */
export type StatusKind =
  | 'live'
  | 'sale'
  | 'buy'
  | 'trade'
  | 'expense'
  | 'settle'
  | 'show'
  | 'locals'
  | 'draft'
  | 'synced'
  | 'queued'
  | 'failed'
  // The Shows booking pipeline (vendor#297). The design puts these in this
  // same vocabulary rather than a Shows-local one
  // (`mtb-design-system/templates/vendor-shows/data.js:93-94`), because the
  // list renders them through the same `StatusBadge` the live/complete states
  // use. `attended` is deliberately absent — see `features/events/types.ts`'s
  // `EventPipelineSchema` and `docs/redesign-rulings.md` R-SH2.
  | 'interested'
  | 'committed'
  | 'table_paid'
  // vendor#326 / R-SH6: a show whose window has passed but which is still
  // `active`. A dealer closes out THE DAY AFTER, not at the table, so this is
  // the state they open the app into — and it needs to say so on the list
  // rather than sit there indistinguishable from a show happening right now.
  | 'needs_closeout';

// Colours are the app's own registered Mantine tokens (`brand`, `violet`,
// `cyan`, `ink`, `red`, `amber` — `packages/mtb-theme`), never bare Mantine
// stock names: `gray` is NOT aliased to `ink` and `yellow` is not a
// registered app token, so either would silently render Mantine's default
// scale instead of the design system's palette.
const STATUS: Record<StatusKind, { label: string; color: string; variant?: 'filled' | 'light' }> = {
  live: { label: 'Live', color: 'brand', variant: 'filled' },
  needs_closeout: { label: 'Close it out', color: 'amber', variant: 'filled' },
  sale: { label: 'Sale', color: 'brand' },
  buy: { label: 'Buy', color: 'ink' },
  trade: { label: 'Trade', color: 'violet' },
  expense: { label: 'Expense', color: 'amber' },
  settle: { label: 'Settle-up', color: 'cyan' },
  show: { label: 'Show', color: 'violet' },
  locals: { label: 'Locals', color: 'cyan' },
  draft: { label: 'Draft', color: 'ink' },
  synced: { label: 'Synced', color: 'brand' },
  queued: { label: 'Queued', color: 'amber' },
  failed: { label: 'Failed', color: 'red' },
  interested: { label: 'Interested', color: 'ink' },
  committed: { label: 'Committed', color: 'cyan' },
  table_paid: { label: 'Table paid', color: 'brand' },
};

/** Props for {@link StatusBadge}. */
export interface StatusBadgeProps {
  /** A known {@link StatusKind}, or any string for a state the vocabulary does not (yet) name. */
  kind: StatusKind | (string & {});
  /** Badge size, forwarded to Mantine. Default `sm`. */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Overrides the label text. Defaults to the vocabulary's label for `kind`. */
  children?: ReactNode;
}

/**
 * StatusBadge — the whole status vocabulary in one place, per
 * `templates/vendor-home/data.js`'s `STATUS` map.
 *
 * Shell: any.
 *
 * An unrecognised `kind` still renders (the raw string, neutral colour)
 * rather than throwing — new statuses show up in review before they show up
 * in a crash report.
 */
export function StatusBadge({ kind, size = 'sm', children }: StatusBadgeProps) {
  const s = STATUS[kind as StatusKind] ?? { label: kind, color: 'ink' as const };
  return (
    <Badge
      color={s.color}
      variant={s.variant ?? 'light'}
      size={size}
      tt="none"
      fw={600}
      radius="var(--radius-chip)"
    >
      {children ?? s.label}
    </Badge>
  );
}
